// e-Fiscalization cell for an invoice row: status badge, Fiscalize action, QR verification link, Storno.
import React, { useState } from 'react';
import { CheckCircle2, AlertTriangle, Loader2, QrCode, Send, Ban, Undo2 } from 'lucide-react';
import { apiCall } from './_shared';

const STORNO_ROLES = ['super_admin', 'admin', 'manager', 'accountant'];

const StornoForm = ({ n, onDone, onCancel }) => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const submit = async () => {
    if (reason.trim().length < 3) { setErr('Reason is required (min 3 characters)'); return; }
    if (!window.confirm(`Issue a storno (corrective) invoice cancelling ${n}? This registers a new fiscal invoice with the Tax Administration and cannot be undone.`)) return;
    setBusy(true); setErr('');
    try {
      const r = await apiCall(`/api/admin/fiscal/invoices/${encodeURIComponent(n)}/storno`, { method: 'POST', body: JSON.stringify({ reason: reason.trim() }) });
      onDone(r);
    } catch (e) { setErr(e.message); setBusy(false); }
  };
  return (
    <div className="mt-1 p-2 rounded-lg border border-red-200 bg-red-50 space-y-1 w-[240px]" data-testid={`storno-form-${n}`}>
      <div className="font-semibold text-red-700 flex items-center gap-1"><Undo2 className="w-3 h-3" /> Storno {n}</div>
      <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (printed on the invoice)" className="w-full border rounded px-1.5 py-1 text-xs" data-testid={`storno-reason-${n}`} />
      <div className="flex gap-1">
        <button type="button" onClick={submit} disabled={busy} className="flex-1 bg-red-600 text-white rounded px-2 py-1 font-semibold disabled:opacity-60 inline-flex items-center justify-center gap-1" data-testid={`storno-confirm-${n}`}>
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Ban className="w-3 h-3" />} Issue storno
        </button>
        <button type="button" onClick={onCancel} disabled={busy} className="border rounded px-2 py-1 bg-white" data-testid={`storno-cancel-${n}`}>Cancel</button>
      </div>
      {err && <div className="text-red-700" data-testid={`storno-error-${n}`}>{err}</div>}
    </div>
  );
};

const FiscalCell = ({ invoice, onChanged, userRole }) => {
  const f = invoice.fiscal;
  const [pay, setPay] = useState(f?.payment || 'cash');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [storno, setStorno] = useState(false);
  const n = invoice.invoice_number;
  const canStorno = STORNO_ROLES.includes(userRole) && invoice.kind !== 'storno' && !invoice.storno_by;

  const run = async () => {
    setBusy(true); setErr('');
    try { await apiCall(`/api/admin/fiscal/invoices/${encodeURIComponent(n)}/fiscalize`, { method: 'POST', body: JSON.stringify({ payment: pay }) }); onChanged && onChanged(); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  if (f?.status === 'fiscalized') {
    return (
      <div className="text-xs" data-testid={`fiscal-ok-${n}`}>
        <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold"><CheckCircle2 className="w-3.5 h-3.5" /> FIC {String(f.fic).slice(0, 8)}…</span>
        <div className="text-slate-500 font-mono">IIC {String(f.iic).slice(0, 8)}… · #{f.inv_ord_num} · {f.env}{f.inv_type === 'CORRECTIVE' ? ' · CORRECTIVE' : ''}</div>
        <div className="flex items-center gap-2">
          {f.verify_url && <a href={f.verify_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-700 hover:underline" data-testid={`fiscal-verify-${n}`}><QrCode className="w-3 h-3" /> verify</a>}
          {invoice.storno_by && <span className="text-red-600 font-semibold" data-testid={`fiscal-cancelled-${n}`}>cancelled by {invoice.storno_by}</span>}
          {canStorno && !storno && <button type="button" onClick={() => setStorno(true)} className="inline-flex items-center gap-1 text-red-600 hover:underline" data-testid={`storno-open-${n}`}><Undo2 className="w-3 h-3" /> storno</button>}
        </div>
        {storno && <StornoForm n={n} onCancel={() => setStorno(false)} onDone={() => { setStorno(false); onChanged && onChanged(); }} />}
      </div>
    );
  }
  return (
    <div className="text-xs space-y-1" data-testid={`fiscal-pending-${n}`}>
      {f?.status === 'failed' && <div className="inline-flex items-center gap-1 text-red-600" title={f.error} data-testid={`fiscal-failed-${n}`}><AlertTriangle className="w-3.5 h-3.5" /> failed · retry</div>}
      <div className="flex items-center gap-1">
        <select value={pay} onChange={(e) => setPay(e.target.value)} className="border rounded px-1 py-0.5" data-testid={`fiscal-pay-${n}`}>
          <option value="cash">Cash</option><option value="card">Card</option><option value="bank_transfer">Bank transfer</option><option value="ota">OTA prepaid</option><option value="other">Other</option>
        </select>
        <button type="button" onClick={run} disabled={busy} className="inline-flex items-center gap-1 bg-slate-900 text-white rounded px-2 py-0.5 font-semibold disabled:opacity-60" data-testid={`fiscal-run-${n}`}>
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Fiscalize
        </button>
      </div>
      {err && <div className="text-red-600 max-w-[220px]" data-testid={`fiscal-error-${n}`}>{err}</div>}
    </div>
  );
};

export default FiscalCell;
